import { Button, FormControl, FormLabel, Link, Text } from '@chakra-ui/react'
import {
  Select as SelectSearchable,
  SingleValue,
  ActionMeta,
} from 'chakra-react-select'
import { chain } from 'lodash'
import { useState } from 'react'
import { SelectedFields } from '../../../types/Regimen'

interface Option {
  label: string
  value: string
  isSelected: boolean
}

interface SelectFieldProps {
  list: {
    label: string
    fieldId: string
    items: { [itemId: string]: string }
    id: string
  }
  selectedFields: SelectedFields
  setSelectedFields: React.Dispatch<React.SetStateAction<SelectedFields>>
  index: number
  editingId?: string
  getBrandIfDuplicate?: (regimenId: string) => string
  onDuplicateCancel?: () => void
}

export default function SelectField({
  list,
  selectedFields,
  setSelectedFields,
  index,
  editingId,
  getBrandIfDuplicate,
  onDuplicateCancel,
}: SelectFieldProps) {
  const [missingVisible, setMissingVisible] = useState(false)
  const isOptional = list.label === 'Brand'

  let placeholder = `Choose ${list.label}`
  if (list.label === 'Brand') {
    placeholder = `Choose ${list.label} or *Unspecified`
  }

  const options = chain(list.items)
    .map((label, id) => ({ label, id }))
    .orderBy((item) => item.label.toLowerCase(), 'asc')
    .value()
    .map(({ id, label }) => {
      const isSelected = selectedFields[list.fieldId] === id
      if (isSelected) placeholder = label
      return { label, value: id, isSelected }
    })

  const handleChange = (
    option: SingleValue<Option>,
    _meta: ActionMeta<Option>,
  ) => {
    if (!option) return

    if (getBrandIfDuplicate) {
      const brand = getBrandIfDuplicate(option.value)
      if (brand) {
        const allowDuplicate = window.confirm(
          `${option.label} with the brand ${brand} is currently active in your list. Are you sure you want to add it?`,
        )
        if (!allowDuplicate && onDuplicateCancel) {
          onDuplicateCancel()
          return
        }
      }
    }

    setSelectedFields({
      ...selectedFields,
      [list.fieldId]: option.value,
    })
  }

  return (
    <FormControl isRequired={!isOptional}>
      <FormLabel>
        {list.label}
        {isOptional ? ' (Optional)' : ''}
      </FormLabel>
      <SelectSearchable
        options={options}
        isDisabled={!!editingId && index === 0}
        required={!!editingId && index === 0}
        isOptionSelected={(
          option: unknown,
          selectValue: ReadonlyArray<unknown>,
        ) => (option as Option).isSelected}
        filterOption={(option: unknown, inputValue: string) =>
          (option as Option).label
            .toLowerCase()
            .includes(inputValue.toLowerCase())
        }
        onChange={handleChange}
        placeholder={placeholder}
      />
      {missingVisible ? (
        <Text fontSize='md'>
          {list.label === 'Supplement' ? (
            <>
              If your supplement is not listed, please email{' '}
              <Link
                href='mailto:registry@healingals.org'
                color='blue.500'
                textDecoration='underline'
              >
                registry@healingals.org
              </Link>
            </>
          ) : (
            <>
              If your brand is not listed, you may use &ldquo;generic or
              unspecified&rdquo;, or if you prefer to track a specific brand,
              email{' '}
              <Link
                href='mailto:registry@healingals.org'
                color='blue.500'
                textDecoration='underline'
              >
                registry@healingals.org
              </Link>
            </>
          )}
        </Text>
      ) : (
        <Button
          size='md'
          variant='link'
          onClick={() => setMissingVisible(true)}
        >
          {list.label === 'Supplement'
            ? 'Supplement not listed?'
            : 'Brand Missing?'}
        </Button>
      )}
    </FormControl>
  )
}
